import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, ChevronDown, Settings, Info, ArrowLeft, Copy, Check, Trash2, Loader2, ExternalLink,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

// ── List view ───────────────────────────────────────────────────────────────
function AccountList({ accounts, loading, onAdd, onEdit, onInfo, onDelete }) {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <DialogTitle className="font-display text-navy text-lg">WhatsApp Configuration</DialogTitle>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-navy text-white hover:bg-navy-hover gap-1">
              <Plus className="h-4 w-4" /> Connect <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onAdd}>Manual Configuration</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-lg border border-navy/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium w-16">S.NO</th>
              <th className="px-4 py-2.5 font-medium">TITLE</th>
              <th className="px-4 py-2.5 font-medium">PROVIDER</th>
              <th className="px-4 py-2.5 font-medium text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                No WhatsApp accounts yet. Click <strong>Connect</strong> to add one.
              </td></tr>
            ) : (
              accounts.map((a, i) => (
                <tr key={a.id} className="border-t border-navy/5">
                  <td className="px-4 py-3">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-navy">{a.title}</td>
                  <td className="px-4 py-3">{a.provider}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => onEdit(a)} title="Edit" className="text-navy hover:text-navy-hover">
                        <Settings className="h-4 w-4" />
                      </button>
                      <button onClick={() => onInfo(a)} title="Details" className="text-navy hover:text-navy-hover">
                        <Info className="h-4 w-4" />
                      </button>
                      <button onClick={() => onDelete(a)} title="Remove" className="text-red-500 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Read-only field with copy ─────────────────────────────────────────────────
function ReadOnlyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Failed to copy"); }
  };
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1 flex items-center gap-2 rounded-md border border-navy/10 bg-cream/40 px-3 py-2">
        <code className="text-xs text-ink break-all flex-1">{value || "—"}</code>
        {value && (
          <button onClick={copy} className="text-navy shrink-0">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Add / Edit form (Meta WABA, Manual Configuration) ─────────────────────────
function AccountForm({ account, onCancel, onSaved }) {
  const isEdit = !!account;
  const [form, setForm] = useState({
    title: account?.title || "",
    api_key: "",
    waba_id: account?.waba_id || "",
    phone_number_id: account?.phone_number_id || "",
    display_phone_number: account?.display_phone_number || "",
    project: account?.project || "",
    marketing_enabled: account?.marketing_enabled || false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.title || !form.waba_id || !form.phone_number_id || !form.display_phone_number) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!isEdit && !form.api_key) {
      toast.error("API Key is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/whatsapp/accounts/${account.id}`, form);
        toast.success("Account updated");
      } else {
        await api.post("/whatsapp/accounts", { ...form, provider: "Meta WABA" });
        toast.success("Account connected");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <button onClick={onCancel} className="text-navy"><ArrowLeft className="h-4 w-4" /></button>
        <DialogTitle className="font-display text-navy text-lg">Meta WABA</DialogTitle>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Fill in the below details</p>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        <div>
          <Label>Title<span className="text-red-500">*</span></Label>
          <Input value={form.title} onChange={set("title")} placeholder="business whatsapp" className="mt-1" />
        </div>
        <div>
          <Label>API Key<span className="text-red-500">*</span></Label>
          <Input
            type="password" value={form.api_key} onChange={set("api_key")}
            placeholder={isEdit ? "•••••••• (leave blank to keep current)" : "Permanent access token"}
            className="mt-1"
          />
        </div>
        {isEdit && account?.callback_key && <ReadOnlyField label="Callback Key" value={account.callback_key} />}
        {isEdit && account?.verify_token && <ReadOnlyField label="Verify Token" value={account.verify_token} />}
        <div>
          <Label>WhatsApp API ID<span className="text-red-500">*</span></Label>
          <Input value={form.waba_id} onChange={set("waba_id")} placeholder="WhatsApp Business Account ID" className="mt-1" />
        </div>
        <div>
          <Label>WhatsApp Phone Number ID<span className="text-red-500">*</span></Label>
          <Input value={form.phone_number_id} onChange={set("phone_number_id")} placeholder="e.g. 851472434705847" className="mt-1" />
        </div>
        <div>
          <Label>Display Phone Number<span className="text-red-500">*</span></Label>
          <Input value={form.display_phone_number} onChange={set("display_phone_number")} placeholder="e.g. 8976410049" className="mt-1" />
        </div>
        <div>
          <Label>Project</Label>
          <Input value={form.project} onChange={set("project")} placeholder="e.g. Sales" className="mt-1" />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Switch checked={form.marketing_enabled} onCheckedChange={set("marketing_enabled")} />
          <Label className="cursor-pointer">Enable for Marketing Campaigns</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          {isEdit ? "Save" : "Next"}
        </Button>
      </div>
    </>
  );
}

// ── Detail view (QR + wa.me + 30-day stats) ──────────────────────────────────
function AccountDetail({ account, onBack }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let objectUrl = null, cancelled = false;
    api.get(`/whatsapp/accounts/${account.id}/qr`, { responseType: "blob" })
      .then((res) => { if (!cancelled) { objectUrl = URL.createObjectURL(res.data); setQrUrl(objectUrl); } })
      .catch(() => {});
    api.get(`/whatsapp/accounts/${account.id}/stats`)
      .then((res) => !cancelled && setStats(res.data))
      .catch(() => !cancelled && setStats({}));
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [account.id]);

  const waLink = stats?.wa_link || "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(waLink); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Failed to copy"); }
  };

  const Stat = ({ label, value }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-navy">{value ?? "-"}</span>
    </div>
  );

  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <button onClick={onBack} className="text-navy"><ArrowLeft className="h-4 w-4" /></button>
        <DialogTitle className="font-display text-navy text-lg">{account.title}</DialogTitle>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{account.display_phone_number}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-navy/10 p-4 flex flex-col items-center justify-center gap-3">
          {qrUrl
            ? <img src={qrUrl} alt="WhatsApp QR" className="h-40 w-40" />
            : <div className="h-40 w-40 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          {waLink && (
            <div className="flex items-center gap-1 text-xs text-navy">
              <a href={waLink} target="_blank" rel="noreferrer" className="underline truncate max-w-[160px] inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> {waLink.replace("https://", "")}
              </a>
              <button onClick={copyLink}>{copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}</button>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-navy/10 p-4">
          <p className="text-sm font-medium text-navy mb-2">Statistics (Last 30 Days)</p>
          {stats === null ? (
            <div className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : (
            <>
              <Stat label="Quality Rating" value={stats.quality_rating} />
              <Stat label="Messages Sent" value={stats.sent} />
              <Stat label="Messages Delivered" value={stats.delivered} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Container ─────────────────────────────────────────────────────────────────
export default function WhatsAppConfigModal({ open, onOpenChange }) {
  const [view, setView] = useState("list");   // list | form | detail
  const [active, setActive] = useState(null);  // account being edited/viewed
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/accounts");
      setAccounts(data);
    } catch {
      toast.error("Failed to load WhatsApp accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setView("list"); setActive(null); load(); }
  }, [open, load]);

  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.title}"?`)) return;
    try {
      await api.delete(`/whatsapp/accounts/${a.id}`);
      toast.success("Account removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader className="sr-only"><DialogTitle>WhatsApp Configuration</DialogTitle></DialogHeader>

        {view === "list" && (
          <AccountList
            accounts={accounts} loading={loading}
            onAdd={() => { setActive(null); setView("form"); }}
            onEdit={(a) => { setActive(a); setView("form"); }}
            onInfo={(a) => { setActive(a); setView("detail"); }}
            onDelete={remove}
          />
        )}
        {view === "form" && (
          <AccountForm
            account={active}
            onCancel={() => setView("list")}
            onSaved={() => { setView("list"); load(); }}
          />
        )}
        {view === "detail" && active && (
          <AccountDetail account={active} onBack={() => setView("list")} />
        )}
      </DialogContent>
    </Dialog>
  );
}
